<template>
  <div class="typeahead">
    <label v-if="label" :for="id">{{ label }}</label>
    <small v-if="helper" class="form-text form-helper">{{ helper }}</small>

    <div class="position-relative">
      <button v-if="selection" class="clear" @click.prevent="clear">
        <i class="fa fa-times-circle"></i>
      </button>
      <button v-if="!selection && !showingAll" class="clear" @click.prevent="(showingAll = true), (results = true)">
        <i class="fa fa-caret-down"></i>
      </button>
      <button v-if="!selection && showingAll" class="clear" @click.prevent="(showingAll = false), (results = false)">
        <i class="fa fa-caret-up"></i>
      </button>
      <input
        v-bind="$attrs"
        v-model="query"
        class="form-control"
        :class="{
          success: !hasError && query,
          error: hasError,
        }"
        type="text"
        @input="searching"
      />

      <ul class="suggestions list-group" v-if="results">
        <button
          v-for="(match, index) in matches"
          :key="index"
          :class="{ error: hasError }"
          class="list-group-item list-group-item-action flex-shrink-0"
          type="button"
          @click.prevent="select(index)"
        >
          <slot name="item" :match="match">{{ match[searchField] }}</slot>
        </button>

        <button
          v-if="!matches.length"
          class="list-group-item list-group-item-action flex-shrink-0"
          @click.prevent="noResultsTriggered"
        >
          <slot name="noResults">
            <p>Sorry, we couldn't find any matches</p>
          </slot>
        </button>

        <button
          v-if="matches.length && showListAppend"
          class="list-group-item list-group-item-action flex-shrink-0"
          @click.prevent="listAppendTriggered"
        >
          <slot name="listAppend" />
        </button>
      </ul>
    </div>

    <Error v-if="hasError" :errors="errors" />
  </div>
</template>

<script>
import Error from '@/Shared/Inputs/Error';

export default {
  inheritAttrs: false,

  components: {
    Error,
  },

  props: {
    id: {
      type: String,
      default() {
        // eslint-disable-next-line no-underscore-dangle
        return `text-input-${this._uid}`;
      },
    },
    returnField: {
      type: String,
      required: false,
    },
    idField: {
      type: String,
      default: 'id',
    },
    searchField: {
      type: [Array, String],
      default: 'email',
    },
    displayField: {
      type: [String],
      default: 'email',
    },
    errors: {
      type: Array,
      default: () => [],
    },
    tolerance: {
      type: Number,
      default: 2,
    },
    value: [String, Object, Number],
    label: String,
    helper: String,
    name: String,
    items: Array,
    showListAppend: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      query: '',
      results: false,
      selection: this.value || null,
      showingAll: false,
    };
  },

  mounted() {
    if (this.value) this.setValue(this.value);
  },

  watch: {
    value(value) {
      this.setValue(value);
    },
  },

  computed: {
    metSearchTolerance() {
      return this.query.length > this.tolerance;
    },

    hasError() {
      return this.errors && this.errors.length > 0;
    },

    matches() {
      function propComparator(key) {
        return function (a, b) {
          const nameA = a[key].toUpperCase();
          const nameB = b[key].toUpperCase();

          let comparison = 0;
          if (nameA > nameB) {
            comparison = 1;
          } else if (nameA < nameB) {
            comparison = -1;
          }
          return comparison;
        };
      }

      const key = typeof this.searchField === 'object'
        ? this.searchField[0]
        : this.searchField;

      if (this.showingAll) {
        return this.items;
      }

      if (this.query === '') {
        return [];
      }

      if (!this.metSearchTolerance) {
        return [];
      }

      let results;
      if (typeof this.searchField === 'object') {
        results = [];
        this.searchField.forEach((field) => {
          results.push(
            this.items.filter(
              (item) => item[field].toLowerCase().indexOf(this.query.toLowerCase()) >= 0,
            ),
          );
        });

        results = [...new Set(results.flat())];
      } else {
        results = this.items.filter(
          (item) => item[this.searchField]
            .toLowerCase()
            .indexOf(this.query.toLowerCase()) >= 0,
        );
      }

      return results.sort(propComparator(key));
    },
  },

  methods: {
    setValue(value) {
      if (typeof value === 'object') {
        this.query = value[this.displayField];
      }

      if (typeof value === 'string') {
        this.query = value;
      }

      if (typeof value === 'number') {
        this.query = this.items[value][this.displayField];
      }
    },

    showAll() {
      this.showingAll = !this.showingAll;
    },

    searching() {
      this.showingAll = false;
      if (!this.metSearchTolerance) {
        this.results = false;
      }
      if (this.metSearchTolerance && this.results === false) {
        this.results = true;
      }
    },

    select(index) {
      this.results = false;
      this.selection = this.matches[index];
      this.$emit('input', this.matches[index]);
      this.$emit('selected', this.matches[index]);
    },

    clear() {
      this.query = '';
      this.showingAll = false;
      this.results = false;
      this.selection = null;
      this.$emit('input', '');
      this.$emit('selected', null);
    },

    noResultsTriggered() {
      this.$emit('noResults', this.query);
      this.clear();
    },

    listAppendTriggered() {
      this.$emit('listAppend', this.query);
      this.clear();
    },
  },
};
</script>
